import * as THREE from 'three';
import { InteractiveStar } from './InteractiveStar.js';

const SKY_RADIUS = 800;

function azAlt(azDeg, altDeg) {
  const az = (azDeg * Math.PI) / 180;
  const alt = (altDeg * Math.PI) / 180;
  const cosAlt = Math.cos(alt);
  return new THREE.Vector3(
    SKY_RADIUS * cosAlt * Math.sin(az),
    SKY_RADIUS * Math.sin(alt),
    -SKY_RADIUS * cosAlt * Math.cos(az),
  );
}

const KERALA_CARD = `
  <h2>Kerala</h2>
  <div class="label">First home</div>

  <p>This is where it all started. This is the place where you grew up.</p>

  <p>You spent your childhood here - running, dancing, learning how to be a person. And somewhere along the way, you met people who never left. The kind of friends that start as classmates and turn into family without anyone noticing.</p>

  <div class="friends">
    <div class="friend">Sree</div>
    <div class="friend">Abhi</div>
    <div class="friend">Arun</div>
    <div class="friend">Hrishika</div>
  </div>

  <p>Sree showed up somewhere in the middle of school and never left. The one who finishes your sentences, steals your food, and somehow always knows what you are thinking before you do.</p>

  <p>You have lived in other places since. But Kerala will always be your HOME.</p>
`;

const BANGALORE_CARD = `
  <h2>Bangalore</h2>
  <div class="label">Second home</div>

  <p>This is the place where you became yourself.</p>
  <p>You came here for college, which is the polite name for the place where you stop being a child and start being someone who has to figure out who they are. You spent four years figuring out. You did not finish figuring out - nobody does - but you did the most important version of it here.</p>
  <p>You met a lot of people - but only a few stayed once it was over. These are the ones that count.</p>

  <div class="friends">
    <div class="friend">Dhairya</div>
    <div class="friend">Sadia</div>
    <div class="friend">Shalini</div>
    <div class="friend">Garvit</div>
  </div>

  <p>You don't live here anymore. But you grew up here. And the people who stayed - they are still yours.</p>
`;

const SEATTLE_CARD = `
  <h2>Seattle</h2>
  <div class="label">Third home</div>

  <p>You came here alone.</p>

  <p>You crossed half the planet by yourself to start this chapter. You did not make a thing of it. You just packed, and flew, and landed, and started.</p>

  <p>The bravest thing you've done is also the quietest.</p>

  <p>You built a real life here. You are a real hard worker. You have made more in your years than most people make in twice as long, and you almost never say it about yourself. So this universe will.</p>

  <p>There is a thing you wished, even though you didn't say it often. You wished your closest people were closer to this place. Some could not come.</p>

  <p>So you found new ones.</p>

  <div class="friends">
    <div class="friend">Bhagi</div>
    <div class="friend">Rohit</div>
    <div class="friend">Tarun</div>
    <div class="friend">Anand</div>
    <div class="friend">Divya</div>
    <div class="friend">Animesh</div>
    <div class="friend">Bansari</div>
    <div class="friend">Anshul</div>
    <div class="friend">Nova</div>
    <div class="friend">Thibaud</div>
    <div class="friend">Chiradeep</div>
  </div>

  <p>You wished they were closer. They are. They have been. This whole world has been quietly orbiting you the entire time.</p>
`;

/**
 * Create the Three Home Planets - Kerala, Bangalore, Seattle.
 *
 * Now rendered as **interactive star sprites** (dots), not 3D textured
 * spheres. Real planets in the night sky look like points of light, and
 * making them spheres broke the "this is your real sky" illusion. Each
 * planet has a distinct color tint so they're visually identifiable.
 */
export function createHomePlanets({ onClick }) {
  const kerala = new InteractiveStar({
    position: azAlt(30, 25),
    color: 0x9ce0b0,
    glowColor: '140, 215, 170',
    scale: 26,
    pulseSpeed: 0.5,
    pulseAmount: 0.04,
    discoveryId: 'planet-kerala',
    category: 'places',
    cardHTML: KERALA_CARD,
    onClick,
  });

  const bangalore = new InteractiveStar({
    position: azAlt(140, 35),
    color: 0xffc090,
    glowColor: '230, 160, 100',
    scale: 26,
    pulseSpeed: 0.5,
    pulseAmount: 0.04,
    discoveryId: 'planet-bangalore',
    category: 'places',
    cardHTML: BANGALORE_CARD,
    onClick,
  });

  const seattle = new InteractiveStar({
    position: azAlt(220, 40),
    color: 0xa0c0e8,
    glowColor: '140, 175, 220',
    scale: 26,
    pulseSpeed: 0.5,
    pulseAmount: 0.04,
    discoveryId: 'planet-seattle',
    category: 'places',
    cardHTML: SEATTLE_CARD,
    onClick,
  });

  return [kerala, bangalore, seattle];
}
